<template>
  <div class="em-form">
    <div class="em-form__header">
      <h3 class="em-form__title">{{ isEdit ? 'Edit' : 'Create New' }} Event Module<template v-if="isEdit && editData && editData.appName"> ({{ editData.appName }})</template></h3>
      <button class="em-form__close" @click="$emit('close')">
        <v-icon size="18">mdi-close</v-icon>
      </button>
    </div>

    <!-- Tabs -->
    <div class="em-tabs">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        class="em-tabs__item"
        :class="{ 'em-tabs__item--active': activeTab === tab.key }"
        @click="activeTab = tab.key"
      >
        <v-icon size="14" class="mr-1">{{ tab.icon }}</v-icon>
        {{ tab.label }}
      </button>
    </div>

    <div class="em-form__body">
      <!-- Tab: Basic Info -->
      <div v-show="activeTab === 'basic'">
        <div class="em-form__group">
          <label class="em-form__label">
            App Name <span class="req">*</span>
            <FieldGuideHelp field-key="appName" />
          </label>
          <v-text-field
            dense outlined hide-details="auto"
            placeholder="e.g. HRDC New Event"
            v-model="form.appName"
            :error-messages="errors.appName"
            class="em-input"
          ></v-text-field>
        </div>

        <div class="em-form__row">
          <div class="em-form__group">
            <label class="em-form__label">Service Key <span class="req">*</span></label>
            <v-text-field
              dense outlined hide-details="auto"
              placeholder="e.g. hrdc_new_event"
              v-model="form.serviceKey"
              disabled
              :error-messages="errors.serviceKey"
              class="em-input"
            ></v-text-field>
            <span class="em-form__hint" v-if="!isEdit">Auto-generated from App Name. Cannot be changed after creation.</span>
          </div>
          <div class="em-form__group">
            <label class="em-form__label">HRDC Type <span class="req">*</span></label>
            <v-text-field
              dense outlined hide-details="auto"
              type="number"
              v-model="form.hrdcType"
              disabled
              :error-messages="errors.hrdcType"
              class="em-input"
            ></v-text-field>
            <span class="em-form__hint">Auto-assigned based on total modules</span>
          </div>
        </div>

        <div class="em-form__group em-form__checkboxes">
          <v-checkbox v-model="form.isSingleDay" hide-details dense>
            <template v-slot:label>
              <span>Single Day Event</span>
              <FieldGuideHelp field-key="isSingleDay" />
            </template>
          </v-checkbox>
          <v-checkbox v-model="form.hasAttendanceList" hide-details dense>
            <template v-slot:label>
              <span>Has Attendance List</span>
              <FieldGuideHelp field-key="hasAttendanceList" />
            </template>
          </v-checkbox>
          <v-checkbox v-model="form.isOnlineEvent" label="Is Online Event?" hide-details dense></v-checkbox>
        </div>

        <div class="em-form__row">
          <div class="em-form__group">
            <label class="em-form__label">
              Icon
              <FieldGuideHelp field-key="icon" />
            </label>
            <div class="em-icon-grid">
              <button
                v-for="ic in iconOptions"
                :key="ic"
                type="button"
                class="em-icon-btn"
                :class="{ 'em-icon-btn--active': form.icon === ic }"
                @click="form.icon = ic"
              >
                <v-icon size="20">{{ ic }}</v-icon>
              </button>
            </div>
          </div>
          <div class="em-form__group">
            <label class="em-form__label">
              Accent Color
              <FieldGuideHelp field-key="accentColor" />
            </label>
            <div class="em-color-grid">
              <button
                v-for="col in colorOptions"
                :key="col.value"
                type="button"
                class="em-color-btn"
                :class="{ 'em-color-btn--active': form.accentColor === col.value }"
                :style="{ background: col.value }"
                @click="form.accentColor = col.value"
                :title="col.label"
              ></button>
            </div>
          </div>
        </div>

        <div class="em-form__group">
          <label class="em-form__label">
            Name 1 (Short) <span class="req">*</span>
            <FieldGuideHelp field-key="name1" />
          </label>
          <v-text-field dense outlined hide-details="auto" v-model="form.name1" placeholder="e.g. New Event" class="em-input"></v-text-field>
        </div>
      </div>

      <!-- Tab: Email Template -->
      <div v-show="activeTab === 'email'">
        <!-- Create-mode: copy the email template HTML from an existing module -->
        <div v-if="!isEdit" class="em-atch-card em-tpl-copy">
          <div class="em-atch-card__header">
            <v-icon size="18" color="var(--app-accent)" class="mr-2">mdi-content-duplicate</v-icon>
            <div class="em-atch-card__title">Use an Existing Email Template</div>
          </div>
          <div class="em-form__hint em-tpl-copy__hint">
            Pick a module to copy its email template HTML. You can review and
            edit it below before creating.
          </div>
          <ModuleTemplatePicker
            v-if="modules.length"
            :value="emailSourceUuid"
            :modules="modules"
            placeholder="Select a module to copy the template from…"
            @select="selectEmailSource"
          />
          <div v-else class="em-form__hint">No existing modules available to copy from.</div>
        </div>

        <!-- Editor + live preview (available in both create and edit) -->
        <div class="em-tpl-split">
          <!-- HTML Editor -->
          <div class="em-tpl-split__panel">
            <div class="em-form__label">Template HTML</div>
            <textarea
              v-model="form.emailTemplateContent"
              class="em-tpl-editor"
              placeholder="Paste or write your email template HTML here..."
            ></textarea>
          </div>

          <!-- Live Preview -->
          <div class="em-tpl-split__panel">
            <div class="em-form__label">Preview</div>
            <div class="em-tpl-preview__frame" v-html="form.emailTemplateContent"></div>
          </div>
        </div>
      </div>

      <!-- Tab: Pricing -->
      <div v-show="activeTab === 'pricing'">
        <!-- taxPOD Member -->
        <div class="em-pricing-section">
          <div class="em-pricing-section__header">
            <v-icon size="16" color="var(--app-accent)" class="mr-1">mdi-shield-check-outline</v-icon>
            <span class="em-pricing-section__title">taxPOD Member</span>
            <FieldGuideHelp field-key="memberPricing" />
          </div>
          <div class="em-pricing">
            <div class="em-pricing__items" v-if="memberPricingList.length">
              <div class="em-pricing__item" v-for="(price, i) in memberPricingList" :key="'m-'+i">
                <span class="em-pricing__item-price">RM {{ price.toLocaleString() }}</span>
                <div class="em-pricing__item-colors">
                  <button
                    v-for="pc in priceColorOptions"
                    :key="pc.css"
                    type="button"
                    class="em-pricing__color-dot"
                    :class="{ 'em-pricing__color-dot--active': priceColors['member_'+price] === pc.css }"
                    :style="{ background: pc.hex }"
                    :title="pc.label"
                    @click="setPriceColor('member_'+price, pc.css)"
                  ></button>
                </div>
                <button class="em-pricing__item-remove" @click="memberPricingList.splice(i, 1)" type="button">
                  <v-icon size="14" color="var(--app-text-faint)">mdi-close</v-icon>
                </button>
              </div>
            </div>
            <div class="em-pricing__add">
              <v-text-field
                dense outlined hide-details="auto"
                placeholder="Enter member price (RM)"
                v-model="newMemberPrice"
                type="number"
                min="0"
                class="em-input"
                @keydown.enter.prevent="addMemberPrice()"
              ></v-text-field>
              <button class="em-pricing__add-btn" @click="addMemberPrice()" type="button">
                <v-icon size="16" color="#fff">mdi-plus</v-icon>
              </button>
            </div>
          </div>
        </div>

        <!-- Non-taxPOD Member -->
        <div class="em-pricing-section mt-4">
          <div class="em-pricing-section__header">
            <v-icon size="16" color="#f59e0b" class="mr-1">mdi-account-outline</v-icon>
            <span class="em-pricing-section__title">Non-taxPOD Member</span>
            <FieldGuideHelp field-key="nonMemberPricing" />
          </div>
          <div class="em-pricing">
            <div class="em-pricing__items" v-if="nonMemberPricingList.length">
              <div class="em-pricing__item" v-for="(price, i) in nonMemberPricingList" :key="'n-'+i">
                <span class="em-pricing__item-price">RM {{ price.toLocaleString() }}</span>
                <div class="em-pricing__item-colors">
                  <button
                    v-for="pc in priceColorOptions"
                    :key="pc.css"
                    type="button"
                    class="em-pricing__color-dot"
                    :class="{ 'em-pricing__color-dot--active': priceColors['nonmember_'+price] === pc.css }"
                    :style="{ background: pc.hex }"
                    :title="pc.label"
                    @click="setPriceColor('nonmember_'+price, pc.css)"
                  ></button>
                </div>
                <button class="em-pricing__item-remove" @click="nonMemberPricingList.splice(i, 1)" type="button">
                  <v-icon size="14" color="var(--app-text-faint)">mdi-close</v-icon>
                </button>
              </div>
            </div>
            <div class="em-pricing__add">
              <v-text-field
                dense outlined hide-details="auto"
                placeholder="Enter non-member price (RM)"
                v-model="newNonMemberPrice"
                type="number"
                min="0"
                class="em-input"
                @keydown.enter.prevent="addNonMemberPrice()"
              ></v-text-field>
              <button class="em-pricing__add-btn" @click="addNonMemberPrice()" type="button">
                <v-icon size="16" color="#fff">mdi-plus</v-icon>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Tab: Settings -->
      <div v-show="activeTab === 'settings'">
        <div class="em-form__row">
          <div class="em-form__group">
            <label class="em-form__label">
              Table View Key
              <FieldGuideHelp field-key="crmViewKey" />
            </label>
            <v-text-field dense outlined hide-details="auto" v-model="form.crmViewKey" disabled class="em-input"></v-text-field>
            <span class="em-form__hint">Auto-generated from Name 1.</span>
          </div>
          <div class="em-form__group">
            <label class="em-form__label">
              Xero Callback Suffix
              <FieldGuideHelp field-key="xeroCallbackSuffix" />
            </label>
            <v-text-field dense outlined hide-details="auto" v-model="form.xeroCallbackSuffix" disabled class="em-input"></v-text-field>
            <span class="em-form__hint">Auto-generated from Name 1.</span>
          </div>
        </div>

        <div class="em-form__row mt-4">
          <div class="em-form__group em-form__group--full">
            <label class="em-form__label">
              HRDC Grant Programme Name
              <FieldGuideHelp field-key="hrdcGrantProgrammeNamePattern" />
            </label>
            <v-text-field
              dense
              outlined
              hide-details="auto"
              v-model="form.hrdcGrantProgrammeNamePattern"
              placeholder="e.g. E- Invoicing Conference"
              class="em-input"
              persistent-hint
              hint="Exact programme name as it appears after 'PROGRAMME NAME:' in the HRDC grant approval email. Leave blank to skip auto-validation for this module."
            ></v-text-field>
          </div>
        </div>
      </div>

      <!-- Tab: Attachments -->
      <div v-show="activeTab === 'attachments'">
        <!-- Auto-generated PDF filenames -->
        <div class="em-form__section-label">Auto-generated PDF filenames</div>
        <div class="em-form__group">
          <label class="em-form__label">
            Confirmation Letter
            <FieldGuideHelp field-key="confirmationLetterTemplate" />
          </label>
          <v-text-field dense outlined hide-details="auto" v-model="form.confirmationLetterTemplate" disabled class="em-input"></v-text-field>
          <span class="em-form__hint">Auto-generated from Name 1.</span>
        </div>
        <div class="em-form__row">
          <div class="em-form__group">
            <label class="em-form__label">
              JD14 Template
              <FieldGuideHelp field-key="jd14Template" />
            </label>
            <v-text-field dense outlined hide-details="auto" v-model="form.jd14Template" disabled class="em-input"></v-text-field>
            <span class="em-form__hint">Auto-generated from Name 1.</span>
          </div>
          <div class="em-form__group">
            <label class="em-form__label">
              T3 Template
              <FieldGuideHelp field-key="t3Template" />
            </label>
            <v-text-field dense outlined hide-details="auto" v-model="form.t3Template" disabled class="em-input"></v-text-field>
            <span class="em-form__hint">Auto-generated from Name 1.</span>
          </div>
        </div>

        <!-- Upload cards -->
        <div class="em-form__section-label mt-4">Uploads</div>

        <!-- Guideline -->
        <div class="em-atch-card">
          <div class="em-atch-card__header">
            <v-icon size="18" color="var(--app-accent)" class="mr-2">mdi-file-pdf-box</v-icon>
            <div class="em-atch-card__title">
              Guideline
              <FieldGuideHelp field-key="guidelineName" />
            </div>
          </div>

          <div class="em-atch-card__row">
            <v-text-field
              dense outlined hide-details="auto"
              v-model="form.guidelineName"
              placeholder="Enter guideline filename (no extension)"
              class="em-input em-atch-card__name-input"
            ></v-text-field>

            <div
              class="em-atch-card__dropzone"
              @drop.prevent="dropFile($event, 'guideline')"
              @dragover.prevent
            >
              <div v-if="attachmentFiles.guideline" class="em-atch-card__selected">
                <v-icon size="18" color="var(--app-accent)">mdi-file-check-outline</v-icon>
                <span>{{ attachmentFiles.guideline.name }}</span>
                <button type="button" @click.stop="attachmentFiles.guideline = null" class="em-atch-card__remove">
                  <v-icon size="14" color="var(--app-text-faint)">mdi-close</v-icon>
                </button>
              </div>
              <div v-else-if="existingAttachments.guideline" class="em-atch-card__existing">
                <v-icon size="18" color="#10b981">mdi-file-pdf-box</v-icon>
                <span>{{ guidelineFileName }}</span>
              </div>
              <div v-else class="em-atch-card__placeholder">
                <v-icon size="24" color="var(--app-text-faint)">mdi-cloud-upload-outline</v-icon>
                <span>Drop PDF here or click to browse</span>
              </div>
              <input
                type="file"
                accept=".pdf"
                class="em-atch-card__file-input"
                @change="attachmentFiles.guideline = $event.target.files[0]"
              />
            </div>
          </div>

          <div class="em-atch-card__actions">
            <button type="button" class="ev-btn ev-btn--ghost ev-btn--sm" @click="previewAttachment('guideline')">
              <v-icon size="14" class="mr-1">mdi-eye-outline</v-icon> Preview
            </button>
          </div>
        </div>

        <!-- Agenda -->
        <div class="em-atch-card mt-3">
          <div class="em-atch-card__header">
            <v-icon size="18" color="#f59e0b" class="mr-2">mdi-file-pdf-box</v-icon>
            <div class="em-atch-card__title">
              Course Content (Agenda)
              <FieldGuideHelp field-key="agendaAttachmentName" />
            </div>
          </div>

          <div class="em-atch-card__row">
            <v-text-field
              dense outlined hide-details="auto"
              v-model="form.agendaAttachmentName"
              placeholder="Enter agenda filename (no extension)"
              class="em-input em-atch-card__name-input"
            ></v-text-field>

            <div
              class="em-atch-card__dropzone"
              @drop.prevent="dropFile($event, 'agenda')"
              @dragover.prevent
            >
              <div v-if="attachmentFiles.agenda" class="em-atch-card__selected">
                <v-icon size="18" color="#f59e0b">mdi-file-check-outline</v-icon>
                <span>{{ attachmentFiles.agenda.name }}</span>
                <button type="button" @click.stop="attachmentFiles.agenda = null" class="em-atch-card__remove">
                  <v-icon size="14" color="var(--app-text-faint)">mdi-close</v-icon>
                </button>
              </div>
              <div v-else-if="existingAttachments.agenda" class="em-atch-card__existing">
                <v-icon size="18" color="#10b981">mdi-file-pdf-box</v-icon>
                <span>{{ agendaFileName }}</span>
              </div>
              <div v-else class="em-atch-card__placeholder">
                <v-icon size="24" color="var(--app-text-faint)">mdi-cloud-upload-outline</v-icon>
                <span>Drop PDF here or click to browse</span>
              </div>
              <input
                type="file"
                accept=".pdf"
                class="em-atch-card__file-input"
                @change="attachmentFiles.agenda = $event.target.files[0]"
              />
            </div>
          </div>

          <div class="em-atch-card__actions">
            <button type="button" class="ev-btn ev-btn--ghost ev-btn--sm" @click="previewAttachment('agenda')">
              <v-icon size="14" class="mr-1">mdi-eye-outline</v-icon> Preview
            </button>
          </div>
        </div>

        <!-- Attendance Form -->
        <div class="em-atch-card mt-3">
          <div class="em-atch-card__header">
            <v-icon size="18" color="#10b981" class="mr-2">mdi-file-pdf-box</v-icon>
            <div class="em-atch-card__title">
              Attendance Form
            </div>
          </div>

          <div class="em-form__group" style="margin-bottom: 12px;">
            <v-checkbox
              v-model="form.hasAttendanceForm"
              label="Include Attendance Form in Email Client?"
              hide-details
              dense
            ></v-checkbox>
          </div>

          <div
            v-if="form.hasAttendanceForm"
            class="em-atch-card__dropzone"
            @drop.prevent="dropFile($event, 'attendanceForm')"
            @dragover.prevent
          >
            <div v-if="attachmentFiles.attendanceForm" class="em-atch-card__selected">
              <v-icon size="18" color="#10b981">mdi-file-check-outline</v-icon>
              <span>{{ attachmentFiles.attendanceForm.name }}</span>
              <button type="button" @click.stop="attachmentFiles.attendanceForm = null" class="em-atch-card__remove">
                <v-icon size="14" color="var(--app-text-faint)">mdi-close</v-icon>
              </button>
            </div>
            <div v-else-if="existingAttachments.attendanceForm" class="em-atch-card__existing">
              <v-icon size="18" color="#10b981">mdi-file-pdf-box</v-icon>
              <span>{{ attendanceFormFileName }}</span>
            </div>
            <div v-else class="em-atch-card__placeholder">
              <v-icon size="24" color="var(--app-text-faint)">mdi-cloud-upload-outline</v-icon>
              <span>Drop PDF here or click to browse</span>
            </div>
            <input
              type="file"
              accept=".pdf"
              class="em-atch-card__file-input"
              @change="attachmentFiles.attendanceForm = $event.target.files[0]"
            />
          </div>

          <div class="em-atch-card__actions" v-if="form.hasAttendanceForm">
            <button type="button" class="ev-btn ev-btn--ghost ev-btn--sm" @click="previewAttachment('attendanceForm')">
              <v-icon size="14" class="mr-1">mdi-eye-outline</v-icon> Preview
            </button>
          </div>
        </div>
      </div>

      <!-- Tab: PDF Config -->
      <div v-show="activeTab === 'pdfConfig'">
        <PdfConfigPanel
          ref="pdfConfigPanel"
          :module-uuid="editData ? editData.uuid : ''"
          :module-data="editData"
          :has-attendance-form="form.hasAttendanceForm"
          :service-url="serviceUrl"
          :auth-token="auth.token"
          :available-modules="modules"
        />
      </div>
    </div>

    <!-- Error message -->
    <div v-if="submitError" class="em-form__error">{{ submitError }}</div>

    <!-- Actions -->
    <div class="em-form__footer">
      <button class="ev-btn ev-btn--ghost" @click="$emit('close')">Cancel</button>
      <button class="ev-btn ev-btn--primary" @click="submit()" :disabled="submitting">
        <v-progress-circular v-if="submitting" indeterminate size="14" width="2" color="white" class="mr-1"></v-progress-circular>
        {{ isEdit ? 'Update' : 'Create' }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { deriveCrmViewKey } from "@/services/hrdc_event/eventRegistry";
import PdfConfigPanel from "./PdfConfigPanel.vue";
import FieldGuideHelp from "./FieldGuideHelp.vue";
import ModuleTemplatePicker from "./ModuleTemplatePicker.vue";
export default {
  components: { PdfConfigPanel, FieldGuideHelp, ModuleTemplatePicker },
  props: {
    editData: { type: Object, default: null },
    serviceUrl: { type: String, required: true },
    nextHrdcType: { type: Number, default: 1 },
    // Full list of existing event modules — offered as "copy from" templates
    // in the PDF Config tab when creating a brand-new module.
    modules: { type: Array, default: () => [] },
  },
  computed: {
    ...mapState({ auth: (state) => state.auth.data }),
    isEdit() {
      return !!this.editData;
    },
    guidelineFileName() {
      if (this.editData && this.editData.guidelineName) {
        return `${this.editData.guidelineName}.pdf`;
      }
      return "Not configured";
    },
    agendaFileName() {
      if (this.editData && this.editData.agendaAttachmentName) {
        return `${this.editData.agendaAttachmentName}.pdf`;
      }
      return "Not configured";
    },
    attendanceFormFileName() {
      if (this.editData && this.editData.attendanceFormName) {
        return `${this.editData.attendanceFormName}.pdf`;
      }
      return "Not configured";
    },
  },
  data() {
    return {
      form: {
        serviceKey: "",
        hrdcType: null,
        appName: "",
        name1: "",
        confirmationLetterTemplate: "",
        jd14Template: "",
        t3Template: "",
        agendaAttachmentName: "",
        guidelineName: "",
        isSingleDay: false,
        hasAttendanceList: false,
        isOnlineEvent: false,
        hasAttendanceForm: false,
        attendanceFormName: "",
        crmViewKey: "",
        xeroCallbackSuffix: "",
        icon: "mdi-cube-outline",
        accentColor: "#0ea5e9",
        emailTemplateContent: "",
        hrdcGrantProgrammeNamePattern: "",
      },
      activeTab: "basic",
      // create-mode: chosen source module to copy the email template HTML from
      emailSourceUuid: null,
      tabs: [
        { key: "basic", label: "Basic Info", icon: "mdi-information-outline" },
        { key: "attachments", label: "Attachments", icon: "mdi-paperclip" },
        { key: "pricing", label: "Pricing", icon: "mdi-tag-outline" },
        { key: "pdfConfig", label: "PDF Config", icon: "mdi-file-cog-outline" },
        { key: "email", label: "Email Template", icon: "mdi-email-outline" },
        { key: "settings", label: "Settings", icon: "mdi-cog-outline" },
      ],
      memberPricingList: [],
      nonMemberPricingList: [],
      priceColors: {},
      newMemberPrice: "",
      newNonMemberPrice: "",
      priceColorOptions: [
        { label: "Blue", css: "tbl-price--blue", hex: "#0ea5e9" },
        { label: "Green", css: "tbl-price--green", hex: "#10b981" },
        { label: "Purple", css: "tbl-price--purple", hex: "#8b5cf6" },
        { label: "Amber", css: "tbl-price--amber", hex: "#f59e0b" },
        { label: "Red", css: "tbl-price--red", hex: "#ef4444" },
        { label: "Pink", css: "tbl-price--pink", hex: "#ec4899" },
      ],
      iconOptions: [
        "mdi-cube-outline",
        "mdi-receipt-text-outline",
        "mdi-podium",
        "mdi-tools",
        "mdi-stamper",
        "mdi-school-outline",
        "mdi-chart-line",
        "mdi-briefcase-outline",
        "mdi-calendar-star",
        "mdi-file-certificate-outline",
        "mdi-lightbulb-outline",
        "mdi-rocket-launch-outline",
        "mdi-shield-check-outline",
        "mdi-trophy-outline",
        "mdi-account-group-outline",
        "mdi-book-open-outline",
        "mdi-cash-register",
        "mdi-bank-outline",
        "mdi-gavel",
        "mdi-scale-balance",
        "mdi-calculator-variant-outline",
        "mdi-finance",
        "mdi-handshake-outline",
        "mdi-clipboard-text-outline",
        "mdi-file-document-outline",
        "mdi-folder-open-outline",
        "mdi-cog-outline",
        "mdi-earth",
        "mdi-domain",
        "mdi-store-outline",
        "mdi-target",
        "mdi-trending-up",
        "mdi-chart-bar",
        "mdi-chart-pie",
        "mdi-database-outline",
        "mdi-cloud-outline",
        "mdi-laptop",
        "mdi-cellphone",
        "mdi-headset",
        "mdi-microphone-outline",
        "mdi-video-outline",
        "mdi-presentation",
        "mdi-human-male-board",
        "mdi-chair-school",
        "mdi-medal-outline",
        "mdi-star-outline",
        "mdi-heart-outline",
        "mdi-flag-outline",
      ],
      colorOptions: [
        { label: "Blue", value: "#0ea5e9" },
        { label: "Purple", value: "#8b5cf6" },
        { label: "Amber", value: "#f59e0b" },
        { label: "Green", value: "#10b981" },
        { label: "Red", value: "#ef4444" },
        { label: "Pink", value: "#ec4899" },
        { label: "Indigo", value: "#6366f1" },
        { label: "Teal", value: "#14b8a6" },
        { label: "Orange", value: "#f97316" },
        { label: "Slate", value: "#64748b" },
        { label: "Sky", value: "#38bdf8" },
        { label: "Cyan", value: "#06b6d4" },
        { label: "Lime", value: "#84cc16" },
        { label: "Emerald", value: "#059669" },
        { label: "Rose", value: "#f43f5e" },
        { label: "Fuchsia", value: "#d946ef" },
        { label: "Violet", value: "#7c3aed" },
        { label: "Navy", value: "#1e3a5f" },
        { label: "Brown", value: "#92400e" },
        { label: "Charcoal", value: "#334155" },
      ],
      attachmentFiles: { guideline: null, agenda: null, attendanceForm: null },
      existingAttachments: { guideline: false, agenda: false, attendanceForm: false },
      uploadingAtch: false,
      errors: {},
      submitError: null,
      submitting: false,
    };
  },
  watch: {
    "form.appName"(val) {
      if (this.isEdit) return;
      this.form.serviceKey = this.appNameToServiceKey(val);
    },
    "form.name1"(val) {
      const suffix = this.nameToTemplateSuffix(val);

      // Backend lookup keys auto-derived from Name 1.
      // crmViewKey uses the shared canonical sanitizer (no hrdc_ prefix) so it
      // matches eventRegistry.deriveCrmViewKey() exactly; xeroCallbackSuffix
      // keeps the serviceKey-style snake (with hrdc_) it has always used.
      this.form.crmViewKey = deriveCrmViewKey(val);
      const snake = this.appNameToServiceKey(val);
      this.form.xeroCallbackSuffix = snake ? `callback_${snake}` : "";

      // The template filenames (Confirmation Letter / JD14 / T3 / Attendance
      // Form) plus Guideline and Agenda all map to physical PDFs on disk.
      // Only auto-fill them on create — on edit, re-deriving them from Name 1
      // would silently change an existing module's filename pointer while the
      // uploaded PDF keeps its old name, orphaning the file (the serve
      // endpoint then 404s with "File not found").
      if (!this.isEdit) {
        this.form.confirmationLetterTemplate = suffix
          ? `CONFIRMATION_LETTER_${suffix}_TEMPLATE`
          : "";
        this.form.jd14Template = suffix ? `JD14_${suffix}_TEMPLATE` : "";
        this.form.t3Template = suffix ? `T3_${suffix}_TEMPLATE` : "";
        this.form.attendanceFormName = suffix
          ? `ATTENDANCE_FORM_${suffix}_TEMPLATE`
          : "";
        this.form.guidelineName = val
          ? `YYC ${val.toUpperCase()} CONFERENCE - HRDC Grant and Claim Application Procedure`
          : "";
        this.form.agendaAttachmentName = val
          ? `YYC ${val} Comprehensive Conference Course Content`
          : "";
      }
    },
  },
  created() {
    if (this.editData) {
      // Populate form from edit data
      for (const key in this.form) {
        if (this.editData[key] !== undefined && this.editData[key] !== null) {
          this.form[key] = this.editData[key];
        }
      }
      if (this.editData.staticPricing) {
        if (typeof this.editData.staticPricing === "object" && !Array.isArray(this.editData.staticPricing)) {
          // New format: { member: [...], nonMember: [...] }
          this.memberPricingList = [...(this.editData.staticPricing.member || [])];
          this.nonMemberPricingList = [...(this.editData.staticPricing.nonMember || [])];
        } else if (Array.isArray(this.editData.staticPricing)) {
          // Legacy format: flat array - treat all as member pricing
          this.memberPricingList = [...this.editData.staticPricing];
        }
      }
      // Check if existing attachments exist
      this.checkExistingAttachments();

      if (this.editData.priceColorMap && typeof this.editData.priceColorMap === "object") {
        this.priceColors = { ...this.editData.priceColorMap };
      }
    } else {
      // Auto-fill hrdcType for new modules
      this.form.hrdcType = this.nextHrdcType;
    }
  },
  methods: {
    /**
     * Convert App Name to a snake_case serviceKey.
     * "HRDC New Event" -> "hrdc_new_event", "HRDC E-Invoicing" -> "hrdc_e_invoicing".
     *
     * The `hrdc_` prefix is the system-wide marker that identifies a service
     * as an event module (navbar Events submenu, CRM module-list exclusion,
     * etc.). It is enforced here so a module never gets a key that would make
     * it invisible — e.g. App Name "Testing 123" -> "hrdc_testing_123".
     */
    appNameToServiceKey(appName) {
      if (!appName) return "";
      let key = appName
        .toLowerCase()
        .replace(/-/g, "_")
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, "")
        .replace(/_+/g, "_")
        .replace(/^_|_$/g, "");
      if (key && !key.startsWith("hrdc_")) key = `hrdc_${key}`;
      return key;
    },

    /**
     * Convert Name 1 to the SCREAMING_SNAKE template suffix used in the
     * Confirmation Letter / JD14 / T3 filename stems.
     * "e-Invoicing" -> "E_INVOICING", "Tax Summit" -> "TAX_SUMMIT".
     */
    nameToTemplateSuffix(name1) {
      if (!name1) return "";
      return name1
        .toUpperCase()
        .replace(/-/g, "_")
        .replace(/\s+/g, "_")
        .replace(/[^A-Z0-9_]/g, "")
        .replace(/_+/g, "_")
        .replace(/^_|_$/g, "");
    },

    /**
     * Derive technical fields from serviceKey.
     * Converts "hrdc_new_event" -> routePrefix "HrdcNewEvent", etc.
     */
    deriveTechnicalFields(serviceKey) {
      // hrdc_new_event -> NewEvent
      const suffix = serviceKey.replace("hrdc_", "");
      const pascal = suffix
        .split("_")
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
        .join("");

      return {
        routePrefix: `Hrdc${pascal}`,
        pathSuffix: suffix,
        storeModuleName: serviceKey,
        storeMutationName: `assignHrdc${pascal}Data`,
        modelKeyPrefix: `hrdc${pascal}`,
        // Canonical, hrdc_-stripped key (e.g. "Test Event" -> "test_event_application").
        crmViewKey: deriveCrmViewKey(this.form.name1 || serviceKey),
        xeroCallbackSuffix: `callback_${suffix}`,
      };
    },

    addMemberPrice() {
      const val = parseInt(this.newMemberPrice);
      if (!isNaN(val) && val > 0 && !this.memberPricingList.includes(val)) {
        this.memberPricingList.push(val);
        const colorIdx = this.memberPricingList.length - 1;
        this.$set(this.priceColors, "member_" + val, this.priceColorOptions[colorIdx % this.priceColorOptions.length].css);
      }
      this.newMemberPrice = "";
    },
    addNonMemberPrice() {
      const val = parseInt(this.newNonMemberPrice);
      if (!isNaN(val) && val > 0 && !this.nonMemberPricingList.includes(val)) {
        this.nonMemberPricingList.push(val);
        const colorIdx = this.nonMemberPricingList.length - 1;
        this.$set(this.priceColors, "nonmember_" + val, this.priceColorOptions[colorIdx % this.priceColorOptions.length].css);
      }
      this.newNonMemberPrice = "";
    },
    setPriceColor(key, cssClass) {
      this.$set(this.priceColors, key, cssClass);
    },
    /**
     * Create-mode: copy the email template HTML from the chosen source module
     * into the editor. emailTemplateContent is part of the create payload, so
     * whatever ends up in the editor is saved when the module is created.
     * Selecting "None" leaves the current editor content untouched so manual
     * edits aren't lost by an accidental clear.
     */
    selectEmailSource(uuid) {
      this.emailSourceUuid = uuid;
      if (!uuid) return;
      const src = this.modules.find((m) => m.uuid === uuid);
      this.form.emailTemplateContent = (src && src.emailTemplateContent) || "";
    },
    async checkExistingAttachments() {
      if (!this.editData) return;
      const types = {
        guideline: this.editData.guidelineName,
        agenda: this.editData.agendaAttachmentName,
        attendanceForm: this.editData.attendanceFormName,
      };
      for (const [key, name] of Object.entries(types)) {
        if (!name) continue;
        try {
          const filePath = `assets/storage/${name}.pdf`;
          const url = `${this.serviceUrl}/v1/en/console/application/get-attachment-blob?filePath=${encodeURIComponent(filePath)}`;
          const res = await fetch(url, { headers: { Authorization: `Bearer ${this.auth.token}` } });
          if (res.ok) {
            this.existingAttachments[key] = true;
          }
        } catch (e) {
          // File doesn't exist
        }
      }
    },
    dropFile(event, key) {
      const file = event.dataTransfer.files[0];
      if (file && file.type === "application/pdf") {
        this.attachmentFiles[key] = file;
      }
    },
    previewAttachment(fileType) {
      // If a new file is selected, preview locally
      if (this.attachmentFiles[fileType] instanceof File) {
        const url = URL.createObjectURL(this.attachmentFiles[fileType]);
        window.open(url, "_blank");
        return;
      }

      // Otherwise preview from server
      const fileNames = {
        guideline: this.editData ? this.editData.guidelineName : null,
        agenda: this.editData ? this.editData.agendaAttachmentName : null,
        attendanceForm: this.editData ? this.editData.attendanceFormName : null,
      };

      if (!fileNames[fileType]) {
        this.$emit("message", { text: "No file configured", type: "info" });
        return;
      }

      const filePath = `assets/storage/${fileNames[fileType]}.pdf`;
      const url = `${this.serviceUrl}/v1/en/console/application/get-attachment-blob?filePath=${encodeURIComponent(filePath)}`;

      fetch(url, {
        headers: { Authorization: `Bearer ${this.auth.token}` },
        responseType: "blob",
      })
        .then((res) => res.blob())
        .then((blob) => {
          const blobUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
          window.open(blobUrl, "_blank");
        })
        .catch(() => {
          this.$emit("message", { text: "No preview available", type: "info" });
        });
    },
    async uploadAttachments() {
      this.uploadingAtch = true;

      const fd = new FormData();
      const staticFiles = [];

      if (this.attachmentFiles.guideline instanceof File && this.form.guidelineName) {
        fd.append("guideline_filepath", `assets/storage/${this.form.guidelineName}.pdf`);
        fd.append("guideline", this.attachmentFiles.guideline);
        staticFiles.push("guideline");
      }

      if (this.attachmentFiles.agenda instanceof File && this.form.agendaAttachmentName) {
        fd.append("agenda_filepath", `assets/storage/${this.form.agendaAttachmentName}.pdf`);
        fd.append("agenda", this.attachmentFiles.agenda);
        staticFiles.push("agenda");
      }

      if (
        this.form.hasAttendanceForm &&
        this.attachmentFiles.attendanceForm instanceof File &&
        this.form.attendanceFormName
      ) {
        fd.append(
          "attendanceForm_filepath",
          `assets/storage/${this.form.attendanceFormName}.pdf`
        );
        fd.append("attendanceForm", this.attachmentFiles.attendanceForm);
        staticFiles.push("attendanceForm");
      }

      if (staticFiles.length === 0) {
        this.uploadingAtch = false;
        return;
      }

      fd.append("staticFiles", staticFiles.join(","));

      try {
        const response = await fetch(`${this.serviceUrl}/v1/en/console/attachment/update-attachment`, {
          method: "POST",
          headers: { Authorization: `Bearer ${this.auth.token}` },
          body: fd,
        });
        const result = await response.json();

        this.$emit("message", {
          text: result.status ? "Attachments uploaded successfully" : "Upload failed",
          type: result.status ? "success" : "error",
        });

        if (result.status) {
          this.attachmentFiles = { guideline: null, agenda: null };
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.uploadingAtch = false;
      }
    },

    validate() {
      this.errors = {};
      if (!this.form.serviceKey && !this.isEdit) this.errors.serviceKey = "Required";
      if (!this.form.hrdcType && !this.isEdit) this.errors.hrdcType = "Required";
      if (!this.form.appName) this.errors.appName = "Required";
      if (!this.form.name1) this.errors.name1 = "Required";
      return Object.keys(this.errors).length === 0;
    },

    async submit() {
      if (!this.validate()) return;

      this.submitting = true;
      this.submitError = null;

      const staticPricing = {
        member: [...this.memberPricingList],
        nonMember: [...this.nonMemberPricingList],
      };
      const priceColorMap = { ...this.priceColors };

      try {
        let url, method, body;

        if (this.isEdit) {
          url = `${this.serviceUrl}/v1/en/console/event_module/update`;
          method = "PUT";
          body = {
            uuid: this.editData.uuid,
            appName: this.form.appName,
            name1: this.form.name1,
            confirmationLetterTemplate: this.form.confirmationLetterTemplate || null,
            jd14Template: this.form.jd14Template || null,
            t3Template: this.form.t3Template || null,
            agendaAttachmentName: this.form.agendaAttachmentName || null,
            guidelineName: this.form.guidelineName || null,
            staticPricing,
            priceColorMap,
            isSingleDay: this.form.isSingleDay,
            hasAttendanceList: this.form.hasAttendanceList,
            isOnlineEvent: this.form.isOnlineEvent,
            hasAttendanceForm: this.form.hasAttendanceForm,
            attendanceFormName: this.form.attendanceFormName || null,
            crmViewKey: this.form.crmViewKey || null,
            xeroCallbackSuffix: this.form.xeroCallbackSuffix || null,
            icon: this.form.icon || null,
            accentColor: this.form.accentColor || null,
            emailTemplateContent: this.form.emailTemplateContent || null,
            hrdcGrantProgrammeNamePattern:
              this.form.hrdcGrantProgrammeNamePattern || null,
          };
        } else {
          // Derive technical fields
          const technical = this.deriveTechnicalFields(this.form.serviceKey);
          url = `${this.serviceUrl}/v1/en/console/event_module/create`;
          method = "POST";
          body = {
            ...this.form,
            ...technical,
            hrdcType: parseInt(this.form.hrdcType),
            staticPricing,
            priceColorMap,
          };
        }

        const response = await fetch(url, {
          method,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.auth.token}`,
          },
          body: JSON.stringify(body),
        });

        const result = await response.json();

        if (result.status) {
          // Upload attachments if any files were selected (works for both
          // create and edit — the upload endpoint just needs the filepath).
          if (this.attachmentFiles.guideline || this.attachmentFiles.agenda || this.attachmentFiles.attendanceForm) {
            await this.uploadAttachments();
          }
          // Flush PDF Config: upload pending template files + save field rows
          // for every sub-tab in one pass.
          if (this.isEdit && this.$refs.pdfConfigPanel) {
            try {
              await this.$refs.pdfConfigPanel.flushAll();
            } catch (pdfErr) {
              this.submitError = pdfErr.message || "Failed to save PDF config";
              return;
            }
          } else if (
            !this.isEdit &&
            this.$refs.pdfConfigPanel &&
            result.data &&
            result.data.uuid
          ) {
            // New module: persist any PDF field values copied from a template
            // source (attachments are intentionally NOT copied). The module
            // already exists here, so a copy failure is a non-blocking warning
            // rather than a hard error — re-erroring would risk a duplicate
            // create on retry; the admin can re-copy by editing the module.
            try {
              await this.$refs.pdfConfigPanel.persistCopiedFields(
                result.data.uuid
              );
            } catch (pdfErr) {
              this.$emit("message", {
                text:
                  "Module created, but copying PDF fields failed: " +
                  (pdfErr.message || "unknown error"),
                type: "error",
              });
            }
          }
          this.$emit("saved");
        } else {
          this.submitError = result.message || "Failed to save module";
        }
      } catch (err) {
        this.submitError = err.message;
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .em-form {
  background: var(--app-card);
}

.dark-mode .em-form__header {
  background: linear-gradient(135deg, var(--app-surface) 0%, var(--app-accent-soft) 100%);
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .em-form__title {
  color: var(--app-text);
}

.dark-mode .em-form__close {
  border: 1px solid var(--app-border);
  background: var(--app-card);
}

.dark-mode .em-form__close:hover {
  background: var(--app-danger-bg);
}

.dark-mode .em-tabs {
  border-bottom: 2px solid var(--app-border);
}

.dark-mode .em-tabs__item {
  color: var(--app-text-muted);
}

.dark-mode .em-tabs__item:hover {
  color: var(--app-text);
  background: var(--app-surface);
}

.dark-mode .em-tabs__item--active {
  color: var(--app-accent);
  background: var(--app-accent-soft);
  border-bottom-color: var(--app-accent);
}

.dark-mode .em-form__section-label {
  color: var(--app-accent);
  border-bottom: 2px solid var(--app-border);
}

.dark-mode .em-form__label {
  color: var(--app-text);
}

.dark-mode .em-form__hint {
  color: var(--app-text-faint);
}

.dark-mode .em-form__error {
  background: var(--app-danger-bg);
}

.dark-mode .em-form__footer {
  border-top: 1px solid var(--app-border);
  background: var(--app-card);
  background: var(--app-surface);
}

.dark-mode .ev-btn--ghost {
  background: var(--app-card);
  color: var(--app-neutral-fg);
  border: 1px solid var(--app-border);
}

.dark-mode .ev-btn--ghost:hover {
  background: var(--app-elevated);
  border-color: var(--app-border-strong);
}

.dark-mode .em-icon-btn {
  border: 1.5px solid var(--app-border);
  background: var(--app-surface);
}

.dark-mode .em-icon-btn:hover {
  border-color: var(--app-border-strong);
  background: var(--app-card);
}

.dark-mode .em-tpl-vars__btn:hover {
  background: var(--app-accent-soft);
}

.dark-mode .em-tpl-wysiwyg {
  border: 1px solid var(--app-border);
}

.dark-mode .em-tpl-editor {
  color: var(--app-text);
  background: var(--app-surface);
  border: 1px solid var(--app-border);
}

.dark-mode .em-tpl-editor:focus {
  background: var(--app-card);
}

.dark-mode .em-tpl-preview__frame {
  background: var(--app-card);
  border: 1px solid var(--app-border);
}

.dark-mode .em-atch-notice {
  background: var(--app-surface);
  border: 1px dashed var(--app-border-strong);
  color: var(--app-text-muted);
}

.dark-mode .em-atch-card {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
}

.dark-mode .em-atch-card__title {
  color: var(--app-text);
}

.dark-mode .em-atch-card__filename {
  color: var(--app-text-faint);
}

.dark-mode .em-atch-card__dropzone {
  border: 2px dashed var(--app-border-strong);
  background: var(--app-card);
}

.dark-mode .em-atch-card__dropzone:hover {
  background: var(--app-accent-soft);
}

.dark-mode .em-atch-card__placeholder {
  color: var(--app-text-faint);
}

.dark-mode .em-atch-card__selected {
  color: var(--app-text);
}

.dark-mode .em-pricing-section {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
}

.dark-mode .em-pricing-section__title {
  color: var(--app-text);
}

.dark-mode .em-pricing__item {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
}

.dark-mode .em-pricing__item-price {
  color: var(--app-text);
}

.dark-mode .em-pricing__item-remove:hover {
  background: var(--app-danger-bg);
}

/* >>> end auto-dark <<< */

.em-form {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  /* Flex column so body scrolls and footer stays pinned at the bottom. */
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.em-form__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 28px 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #f0f7ff 100%);
  flex: 0 0 auto;
  border-bottom: 1px solid #e2e8f0;
}

.em-form__title {
  font-size: 1.1rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
}

.em-form__close {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.em-form__close:hover {
  background: #fef2f2;
  border-color: #fca5a5;
}

/* Tabs */
.em-tabs {
  display: flex;
  padding: 0 28px;
  gap: 6px;
  border-bottom: 2px solid #f1f5f9;
  flex: 0 0 auto;
}

.em-tabs__item {
  display: inline-flex;
  align-items: center;
  padding: 10px 14px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748b;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: 'Lato', sans-serif;
  border-radius: 8px 8px 0 0;
}

.em-tabs__item:hover {
  color: #334155;
  background: #f8fafc;
}

.em-tabs__item--active {
  color: #0ea5e9;
  background: #f0f7ff;
  border-bottom-color: #0ea5e9;
}

.em-form__body {
  padding: 24px 28px;
  /* Fill remaining vertical space inside the flex column; min-height: 0
     is required so a flex child with overflow can shrink below content size. */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.em-form__section-label {
  font-size: 0.72rem;
  font-weight: 800;
  color: #0ea5e9;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f0f7ff;
  display: flex;
  align-items: center;
}

.em-form__row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.em-form__group {
  margin-bottom: 16px;
}

.em-form__group--full {
  grid-column: 1 / -1;
}

.em-form__label {
  display: block;
  font-size: 0.82rem;
  font-weight: 700;
  color: #334155;
  margin-bottom: 6px;
  line-height: 1.5;
}

.em-form__label .req {
  color: #ef4444;
  font-weight: 700;
}

.em-form__hint {
  display: block;
  font-size: 0.72rem;
  color: #94a3b8;
  margin-top: 4px;
  line-height: 1.4;
  font-style: italic;
}

.em-form__checkboxes {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
}

.em-form__error {
  padding: 10px 28px;
  font-size: 0.82rem;
  font-weight: 600;
  color: #ef4444;
  background: #fef2f2;
  border-left: 3px solid #ef4444;
}

.em-form__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 18px 28px;
  border-top: 1px solid #e2e8f0;
  flex: 0 0 auto;
  background: #fff;
  background: #f8fafc;
}

.ev-btn {
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  border: none;
  font-family: 'Lato', sans-serif;
}

.ev-btn--primary {
  background: #0ea5e9;
  color: #fff;
}

.ev-btn--primary:hover {
  background: #0284c7;
}

.ev-btn--primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.ev-btn--ghost {
  background: #fff;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.ev-btn--ghost:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

/* Icon picker */
.em-icon-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.em-icon-btn {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #f8fafc;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.em-icon-btn:hover {
  border-color: #94a3b8;
  background: #fff;
}

.em-icon-btn--active {
  border-color: #0ea5e9;
  background: #f0f7ff;
  box-shadow: 0 0 0 2px rgba(14, 165, 233, 0.2);
}

/* Color picker */
.em-color-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.em-color-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;
  position: relative;
}

.em-color-btn:hover {
  transform: scale(1.15);
}

.em-color-btn--active {
  border-color: #0f172a;
  box-shadow: 0 0 0 3px rgba(15, 23, 42, 0.15);
}

/* Pricing section */
/* Email template editor */
.em-tpl-vars {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.em-tpl-vars__btn {
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  font-size: 0.74rem;
  font-weight: 600;
  color: #0ea5e9;
  background: #f0f7ff;
  border: 1px solid #bae6fd;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: 'Lato', sans-serif;
}

.em-tpl-vars__btn:hover {
  background: #e0f2fe;
  border-color: #0ea5e9;
}

.em-tpl-wysiwyg {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
}

.em-tpl-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}

.em-tpl-copy {
  margin-bottom: 16px;
}

.em-tpl-copy__hint {
  margin-top: -2px;
  margin-bottom: 10px;
}

.em-tpl-split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  height: calc(80vh - 200px);
  min-width: 0;
  overflow: hidden;
}

.em-tpl-split__panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
}

.em-tpl-split__panel .em-tpl-editor {
  flex: 1;
  min-height: 0;
  resize: none;
  overflow: auto;
  word-break: break-all;
}

.em-tpl-split__panel .em-tpl-preview__frame {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.em-tpl-editor {
  width: 100%;
  min-height: 300px;
  padding: 14px;
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 0.8rem;
  line-height: 1.6;
  color: #1e293b;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  resize: vertical;
  outline: none;
  transition: border-color 0.15s ease;
}

.em-tpl-editor:focus {
  border-color: #0ea5e9;
  background: #fff;
}

.em-tpl-actions {
  display: flex;
  gap: 8px;
  margin: 12px 0;
}

.em-tpl-preview {
  margin-top: 12px;
}

.em-tpl-preview__frame {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
  overflow: auto;
  font-size: 0.85rem;
  height: 100%;
  word-break: break-word;
}


/* Attachment cards */
.em-atch-notice {
  display: flex;
  align-items: center;
  padding: 16px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  font-size: 0.84rem;
  color: #64748b;
  font-weight: 500;
}

.em-atch-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
}

.em-atch-card__header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.em-atch-card__title {
  font-size: 0.85rem;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
}

.em-atch-card__filename {
  font-size: 0.72rem;
  color: #94a3b8;
  font-family: 'SF Mono', 'Fira Code', monospace;
}

.em-atch-card__row {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.em-atch-card__name-input {
  width: 100%;
}

.em-atch-card__dropzone {
  border: 2px dashed #d1d5db;
  border-radius: 10px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fff;
  text-align: center;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.em-atch-card__dropzone:hover {
  border-color: #0ea5e9;
  background: #f0f9ff;
}

.em-atch-card__placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  font-size: 0.78rem;
  color: #94a3b8;
  font-weight: 500;
}

.em-atch-card__selected {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  font-size: 0.82rem;
  font-weight: 600;
  color: #334155;
}

.em-atch-card__existing {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  font-size: 0.82rem;
  font-weight: 600;
  color: #10b981;
}

.em-atch-card__remove {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
}

.em-atch-card__file-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.em-atch-card__actions {
  margin-top: 8px;
  display: flex;
  gap: 8px;
}

.em-atch-upload {
  display: flex;
  justify-content: flex-end;
}

.em-pricing-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
}

.em-pricing-section__header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.em-pricing-section__title {
  font-size: 0.82rem;
  font-weight: 700;
  color: #334155;
}

/* Pricing items */
.em-pricing__items {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.em-pricing__item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.em-pricing__item-price {
  font-size: 0.88rem;
  font-weight: 800;
  color: #0f172a;
  min-width: 90px;
}

.em-pricing__item-colors {
  display: flex;
  gap: 6px;
  flex: 1;
}

.em-pricing__color-dot {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;
}

.em-pricing__color-dot:hover {
  transform: scale(1.2);
}

.em-pricing__color-dot--active {
  border-color: #0f172a;
  box-shadow: 0 0 0 2px rgba(15, 23, 42, 0.15);
}

.em-pricing__item-remove {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  border-radius: 4px;
  transition: background 0.15s ease;
}

.em-pricing__item-remove:hover {
  background: #fef2f2;
}

.em-pricing__add {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.em-pricing__add .em-input {
  flex: 1;
}

.em-pricing__add-btn {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: none;
  background: #0ea5e9;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s ease;
}

.em-pricing__add-btn:hover {
  background: #0284c7;
}

/* ============================================================
   Responsive — tablet (<=959px) & mobile (<=599px).
   Matches the project's Vuetify breakpoints used elsewhere.
   ============================================================ */
@media (max-width: 959px) {
  /* Email template editor + live preview stack vertically and grow
     to a height that works without the fixed 80vh grid. */
  .em-tpl-split {
    grid-template-columns: 1fr;
    height: auto;
    gap: 12px;
    overflow: visible;
  }
  .em-tpl-split__panel {
    min-height: 260px;
    overflow: visible;
  }
  .em-tpl-split__panel .em-tpl-editor {
    min-height: 220px;
  }
  .em-tpl-split__panel .em-tpl-preview__frame {
    min-height: 220px;
  }
}

@media (max-width: 599px) {
  .em-form__header {
    padding: 16px 16px 14px;
  }
  .em-form__title {
    font-size: 1rem;
  }

  /* Six tabs won't fit a phone row — make the strip horizontally
     scrollable instead of wrapping/clipping. */
  .em-tabs {
    padding: 0 12px;
    gap: 2px;
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  .em-tabs::-webkit-scrollbar {
    display: none;
  }
  .em-tabs__item {
    flex: 0 0 auto;
    white-space: nowrap;
    padding: 10px 10px;
  }

  .em-form__body {
    padding: 16px;
  }

  /* Collapse the paired fields and the checkbox grid into one column. */
  .em-form__row {
    grid-template-columns: 1fr;
    gap: 0;
  }
  .em-form__checkboxes {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .em-form__error {
    padding: 10px 16px;
  }

  /* Footer buttons share the full width so they're easy to tap. */
  .em-form__footer {
    padding: 12px 16px;
  }
  .em-form__footer .ev-btn {
    flex: 1;
    justify-content: center;
  }

  /* Smaller icon tiles so more fit per row on a narrow screen. */
  .em-icon-btn {
    width: 34px;
    height: 34px;
  }

  /* Let a pricing row's colour dots wrap below the price/remove line. */
  .em-pricing__item {
    flex-wrap: wrap;
    gap: 8px;
  }
  .em-pricing__item-colors {
    flex-basis: 100%;
    order: 3;
  }
}
</style>

<style>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .em-form .em-input.v-text-field--outlined > .v-input__control > .v-input__slot {
  background: var(--app-card) !important;
}

.dark-mode .em-form .em-input.v-text-field--outlined fieldset {
  border-color: var(--app-border-strong) !important;
}

.dark-mode .em-form .em-input.v-text-field--outlined:hover fieldset {
  border-color: var(--app-border-strong) !important;
}

.dark-mode .em-form .em-input.v-input--is-focused > .v-input__control > .v-input__slot {
  background: var(--app-card) !important;
}

.dark-mode .em-form .em-input .v-text-field__slot input::placeholder,
.dark-mode .em-form .em-input .v-text-field__slot textarea::placeholder {
  color: var(--app-text-faint) !important;
}

.dark-mode .em-form .em-input.v-input--is-disabled > .v-input__control > .v-input__slot {
  background: var(--app-elevated) !important;
}

.dark-mode .em-form .em-input.v-input--is-disabled fieldset {
  border-color: var(--app-border-strong) !important;
}

.dark-mode .em-form .em-input.v-input--is-disabled .v-text-field__slot input {
  color: var(--app-text-muted) !important;
}

.dark-mode .em-form .v-input--checkbox {
  background: var(--app-form-tile);
  border: 1px solid var(--app-form-tile-border);
}

.dark-mode .em-form .v-input--checkbox:hover {
  background: var(--app-form-tile-hover);
  border-color: var(--app-border-strong);
}

.dark-mode .em-form .v-input--checkbox .v-label {
  color: var(--app-text) !important;
}

.dark-mode .em-form .v-input--checkbox .theme--light.v-icon {
  color: var(--app-text-faint) !important;
}

/* >>> end auto-dark <<< */

/* Unscoped: Vuetify input overrides for Event Module form */
.em-form .em-input.v-text-field--outlined > .v-input__control > .v-input__slot {
  border-radius: 10px !important;
  min-height: 42px;
  background: #ffffff !important;
}

.em-form .em-input.v-text-field--outlined fieldset {
  border-color: #cbd5e1 !important;
  transition: all 0.2s ease;
}

.em-form .em-input.v-text-field--outlined fieldset legend {
  width: 0 !important;
  padding: 0 !important;
  max-width: 0 !important;
  overflow: hidden !important;
}

.em-form .em-input .v-label {
  display: none !important;
}

.em-form .em-input.v-text-field--outlined fieldset {
  background: transparent !important;
}

.em-form .em-input.v-text-field--outlined > .v-input__control > .v-input__slot fieldset {
  top: 0 !important;
}

.em-form .em-input.v-text-field--outlined:hover fieldset {
  border-color: #94a3b8 !important;
}

.em-form .em-input.v-input--is-focused fieldset {
  border-color: #0ea5e9 !important;
  border-width: 2px !important;
}

.em-form .em-input.v-input--is-focused > .v-input__control > .v-input__slot {
  background: #fff !important;
}

.em-form .em-input .v-text-field__slot input,
.em-form .em-input .v-text-field__slot textarea {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.85rem !important;
  font-weight: 600 !important;
  color: #1a1e24 !important;
}

.em-form .em-input .v-text-field__slot input::placeholder,
.em-form .em-input .v-text-field__slot textarea::placeholder {
  color: #94a3b8 !important;
  font-weight: 500 !important;
}

.em-form .em-input .v-label {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.85rem !important;
}

.em-form .em-input.v-input--is-disabled > .v-input__control > .v-input__slot {
  background: #e2e8f0 !important;
  cursor: not-allowed !important;
}

.em-form .em-input.v-input--is-disabled fieldset {
  border-color: #cbd5e1 !important;
  border-style: dashed !important;
}

.em-form .em-input.v-input--is-disabled .v-text-field__slot input {
  color: #64748b !important;
  cursor: not-allowed !important;
}

/* Hide number input spinners */
.em-form .em-input input[type="number"]::-webkit-outer-spin-button,
.em-form .em-input input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.em-form .em-input input[type="number"] {
  -moz-appearance: textfield;
}

/* Checkbox styling */
.em-form .v-input--checkbox {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px 14px !important;
  margin: 0 !important;
  transition: all 0.15s ease;
  display: inline-flex;
  width: auto;
}

.em-form .v-input--checkbox:hover {
  border-color: #94a3b8;
}

.em-form .em-form__checkboxes .v-input--checkbox {
  width: 100%;
  display: flex;
}

.em-form .v-input--checkbox .v-label {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.84rem !important;
  font-weight: 600 !important;
  color: #334155 !important;
}

.em-form .v-input--checkbox.v-input--is-label-active {
  background: #f0f7ff;
  border-color: #0ea5e9;
}

.em-form .v-input--checkbox .v-icon {
  font-size: 20px !important;
}

.em-form .v-input--checkbox .theme--light.v-icon {
  color: #cbd5e1 !important;
}

.em-form .v-input--checkbox.v-input--is-label-active .theme--light.v-icon {
  color: #0ea5e9 !important;
}


/* >>> dark companions: the light rules above pin these to a dark ink
   with !important, which on the dark field renders the text invisible
   rather than absent. Token references only. <<< */

.dark-mode .em-form .em-input .v-text-field__slot input,
.dark-mode .em-form .em-input .v-text-field__slot textarea {
  color: var(--app-text) !important;
}

/* Checked state: the light rule paints the tile #f0f7ff, which on dark leaves
   a pale box with the (light) label text invisible on top of it. One extra
   class over the light rule so it wins without !important. */
.dark-mode .em-form .v-input--checkbox.v-input--is-label-active {
  background: var(--app-accent-soft);
  border-color: var(--app-accent-border);
}

.dark-mode .em-form .v-input--checkbox.v-input--is-label-active .v-label {
  color: var(--app-text) !important;
}

.dark-mode .em-form .v-input--checkbox.v-input--is-label-active .theme--light.v-icon {
  color: var(--app-accent) !important;
}
</style>
